import type { HomeHeroPreferences, Meta } from '../../contracts/src/index.ts';
import { contentCertification, heroEligible } from '../../contracts/src/home-hero.ts';
import { metadataHttps } from '../../contracts/src/metadata.ts';
import { catalogExtras, catalogRequest, type CatalogChoice } from './catalogs.ts';
import { SlcError } from './types.ts';

export const homeHeroCatalogs = (choices: CatalogChoice[]) => choices.filter(choice =>
  ['movie', 'series'].includes(choice.catalog.type) && catalogExtras(choice.catalog).every(extra => !extra.isRequired || extra.name === 'skip'));
export const homeHeroCatalogName = (choice: CatalogChoice) =>
  `${choice.catalog.name || choice.catalog.id} ${choice.catalog.type === 'movie' ? 'movies' : 'series'} · ${choice.addonName}`;
export type HomeHeroContent = {
  catalog(choice: CatalogChoice, extra: Record<string, string>, signal: AbortSignal): Promise<Meta[]>;
  metadata(choice: CatalogChoice, meta: Meta, signal: AbortSignal): Promise<Meta | null>;
};

/** Fetch metadata, never artwork, until a bounded shortlist passes the profile's
 * display policy. Both receiver and owner UIs share these request/fallback rules. */
export async function loadHomeHero(choice: CatalogChoice, preferences: HomeHeroPreferences, content: HomeHeroContent, signal: AbortSignal): Promise<Meta[]> {
  const current = () => { if (signal.aborted) throw new SlcError('REQUEST_CANCELLED', 'This banner request was cancelled.'); };
  current();
  if (preferences.source !== 'catalog' || preferences.catalogKey !== choice.key || !homeHeroCatalogs([choice]).length) return [];
  const catalog = await content.catalog(choice, catalogRequest(choice.catalog, {}), signal);
  current();
  const seen = new Set<string>();
  const candidates = catalog.slice(0, 12).filter(meta => {
    if (meta.type !== choice.catalog.type || seen.has(meta.id)) return false;
    seen.add(meta.id); return true;
  });
  const enriched = [...candidates];
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(3, candidates.length) }, async () => {
    while (cursor < candidates.length) {
      current();
      const index = cursor++, meta = candidates[index];
      const score = typeof meta.imdbRating === 'string' && /^\d{1,2}(\.\d+)?$/.test(meta.imdbRating.trim()) ? Number(meta.imdbRating) : NaN;
      if ((preferences.maxCertification === 'all' || contentCertification(meta.contentRating)) &&
          (!preferences.minScore || Number.isFinite(score) && score >= 0 && score <= 10)) continue;
      try {
        const full = await content.metadata(choice, meta, signal); current();
        if (full?.id === meta.id && full.type === meta.type) enriched[index] = full;
      } catch { current(); /* Missing metadata stays subject to the same policy. */ }
    }
  }));
  current();
  return enriched.filter(meta => heroEligible(meta, preferences) && !!metadataHttps(meta.background || meta.poster));
}
